import { motion } from "framer-motion";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as client from "../../client";
import styles from "./SpecialtyReport.module.css";

// Component imports
import { ReportActionMenu } from "./dashboard/ReportActionMenu";
import { DesktopReportHeader } from "./DesktopReportHeader";
import { LayersCard } from "./layers/LayersCard";
import { LossRunCard } from "./loss-run/LossRunCard";
import { PrintableReport } from "./PrintableReport";
import { AddressCard } from "./sov/AddressCard";
import { SummaryCard } from "./summary/SummaryCard";

// Utilities
import { ToastMessage } from "primereact/toast";
import { useLogUIEventCallback, useLogUIEventOnMount } from "../../amplitude";
import { ClientId, featureEnabled } from "../../client-features";
import { useClientId } from "../../ClientConfig";
import {
  fakeCompanyNameFromCompanyName,
  usePrivacyMode,
} from "../../context-providers/Privacy";
import {
  useClearToastsOnUnmount,
  useToast,
} from "../../context-providers/Toast";
import { IfFeatureEnabled } from "../../IfFeatureEnabled";
import { ROUTES } from "../../routes";
import { displayNameForUser, sleepMs } from "../../util";
import { useIsMobile } from "../../responsive/useMediaQuery";
import { MobileReportHeader } from "./mobile/MobileReportHeader";
import { AttachmentCard } from "./attachments/AttachmentCard";
import { applyOverridesToResponse, SafeReportResponse } from "./Conversion";
import { TEMP_PIPELINE_EXAMPLES } from "./dashboard/util";
import {
  useAllUpdatesForReport,
  useCancelJobs,
  useLocalUser,
  useReport,
  useThisReportIsUpdating,
  useUserById,
} from "./data";
import { GeneralInput } from "./general-input/GeneralInput";
import { LossControlReports } from "./LossControlReports";
import { MapModalForSubmission } from "./map/MapModal";
import { addressListSizeSupportsClusterAnalysis } from "./map/util";
import { ReferralCard } from "./referral/ReferralCard";
import { PolicyQuestionsCard } from "./policy/PolicyQuestionsCard";
import { PolicyFieldsCard } from "./policy/PolicyFieldsCard";
import { useBorModal } from "./bor/BorModalContext";
import { useBorForReportBadge, useBorLettersByReport } from "./bor/data";
import { CatCard } from "./cat/CatCard";
import { DeductibleCard } from "./deductible/DeductibleCard";
import { EmailCard } from "./email/EmailCard";
import { Button } from "primereact/button";
import { QuoteIndicationModal } from "./quote-indication/QuoteIndicationModal";
import { LimitsCard } from "./limits/LimitsCard";
import { PolicyFlyModal } from "./policy-fly/PolicyFlyModal";
import {
  LayersMobileSummary,
  LossRunMobileSummary,
  SovMobileSummary,
} from "./mobile/MobileTabSummaries";
import { CatModelingMobile } from "./cat/CatModelingMobile";

interface RoutedTabViewProps {
  rawSpecialtyPropertyInfo: client.ReportResponse;
  safeSpecialtyPropertyInfo: SafeReportResponse;
  setMapModalVisible: ((visible: boolean) => void) | undefined;
}

// Type for tab configuration
interface TabConfig {
  header: string;
  path: string;
  showIf?: (
    info: SafeReportResponse,
    clientId: ClientId,
    user?: client.User
  ) => boolean;
  component:
    | { type: "safe"; component: React.ComponentType<SafeTabComponentProps> }
    | { type: "raw"; component: React.ComponentType<RawTabComponentProps> }
    | {
        type: "safe_and_raw";
        component: React.ComponentType<SafeAndRawComponentProps>;
      };
  // Drop the tab entirely on phone-width screens.
  hideOnMobile?: boolean;
  // On phone-width screens, render this compact summary instead of the full tab.
  mobileSummary?: React.ComponentType<{
    specialtyPropertyInfo: SafeReportResponse;
  }>;
}

// Common props that might be passed to tab components
interface CommonTabComponentProps {
  forPrint?: boolean;
  setMapVisible?: (visible: boolean) => void;
}

type RawTabComponentProps = CommonTabComponentProps & {
  specialtyPropertyInfo: client.ReportResponse;
};

type SafeTabComponentProps = CommonTabComponentProps & {
  specialtyPropertyInfo: SafeReportResponse;
};

type SafeAndRawComponentProps = CommonTabComponentProps & {
  specialtyPropertyInfo: SafeReportResponse;
  rawSpecialtyPropertyInfo: client.ReportResponse;
};

// Define all possible tabs with their conditions
const ALL_TABS: TabConfig[] = [
  {
    header: "General",
    path: "general-input",
    component: { type: "raw", component: GeneralInput },
    hideOnMobile: true,
  },
  {
    header: "Review",
    path: "review",
    component: { type: "safe_and_raw", component: SummaryCard },
    hideOnMobile: true,
  },
  {
    header: "SOV",
    path: "sov",
    component: { type: "raw", component: AddressCard },
    mobileSummary: SovMobileSummary,
  },
  {
    header: "Layers",
    path: "layers",
    component: { type: "safe", component: LayersCard },
    showIf: (_, clientId) => featureEnabled(clientId, "layers-card"),
    mobileSummary: LayersMobileSummary,
  },
  {
    header: "Policy Fields",
    path: "policy-fields",
    component: { type: "safe", component: PolicyFieldsCard },
    showIf: (_, clientId) => featureEnabled(clientId, "policy-fields-card"),
    hideOnMobile: true,
  },
  {
    header: "Modeling",
    path: "cat",
    component: { type: "safe", component: CatCard },
    mobileSummary: CatModelingMobile,
  },
  {
    header: "Loss Run",
    path: "loss-run",
    component: { type: "safe", component: LossRunCard },
    showIf: (_, clientId, user) =>
      featureEnabled(clientId, "loss-run-card") ||
      user?.email === "pantheon_test_user",
    mobileSummary: LossRunMobileSummary,
  },
  {
    header: "Referral",
    path: "referral",
    component: { type: "safe", component: ReferralCard },
    hideOnMobile: true,
  },
  {
    header: "Policy Questions",
    path: "policy-questions",
    component: { type: "safe", component: PolicyQuestionsCard },
    showIf: (_, clientId) => featureEnabled(clientId, "policy-questions-card"),
    hideOnMobile: true,
  },
  {
    header: "Attachments",
    path: "attachments",
    component: { type: "safe", component: AttachmentCard },
    hideOnMobile: true,
  },
  {
    header: "Email",
    path: "email",
    component: { type: "safe", component: EmailCard },
    hideOnMobile: true,
  },
  {
    header: "Limits",
    path: "limits",
    component: { type: "safe", component: LimitsCard },
    showIf: (_, clientId, user) =>
      featureEnabled(clientId, "limits-card", { user }),
    hideOnMobile: true,
  },
  {
    header: "Loss Control Reports",
    path: "loss-control-reports",
    component: { type: "safe", component: LossControlReports },
    showIf: (_, clientId) =>
      featureEnabled(clientId, "loss-control-reports-card"),
    hideOnMobile: true,
  },
  {
    header: "Deductibles",
    path: "deductibles",
    component: { type: "safe", component: DeductibleCard },
    showIf: (_, clientId, user) =>
      featureEnabled(clientId, "deductible-card", { user }),
    hideOnMobile: true,
  },
];

const RoutedTabView: React.FC<RoutedTabViewProps> = ({
  safeSpecialtyPropertyInfo,
  rawSpecialtyPropertyInfo,
  setMapModalVisible,
}) => {
  const clientId = useClientId();
  const navigate = useNavigate();
  const location = useLocation();
  const { tabPath } = useParams<{ tabPath?: string }>();
  const logUIEvent = useLogUIEventCallback();
  const localUser = useLocalUser();
  const isMobile = useIsMobile();

  // Filter tabs based on conditions
  const availableTabs = ALL_TABS.filter((tab) => {
    if (
      tab.showIf &&
      !tab.showIf(safeSpecialtyPropertyInfo, clientId, localUser)
    )
      return false;
    if (isMobile && tab.hideOnMobile) return false;
    return true;
  });

  // Get the current tab from the URL
  const getCurrentTabIndex = () => {
    const index = availableTabs.findIndex((tab) => tab.path === tabPath);
    return index >= 0 ? index : 0;
  };

  // Handle tab changes
  const handleTabChange = (e: { index: number }) => {
    const newPath = availableTabs[e.index]?.path;
    logUIEvent("asgard_click_report_tab", { tab_path: newPath });
    navigate(
      ROUTES.DASHBOARD_REPORT(safeSpecialtyPropertyInfo.report_id, newPath),
      { replace: true }
    );
  };

  // Set initial tab on mount. Preserve the query string: mention emails link
  // to the bare report URL with ?comments=open, which this normalization
  // would otherwise strip before the comment panel can auto-open.
  useEffect(() => {
    const currentPath = location.pathname.split("/").pop();
    if (!currentPath || currentPath === safeSpecialtyPropertyInfo.report_id) {
      navigate(
        ROUTES.DASHBOARD_REPORT(
          safeSpecialtyPropertyInfo.report_id,
          availableTabs[0]?.path,
          location.search
        ),
        { replace: true }
      );
    }
  }, [location.pathname, safeSpecialtyPropertyInfo.report_id]);

  return (
    <TabView
      className={styles.tabView}
      activeIndex={getCurrentTabIndex()}
      onTabChange={handleTabChange}
    >
      {availableTabs.map((tab) => {
        const setMapVisible =
          tab.path === "sov" ? setMapModalVisible : undefined;
        return (
          <TabPanel key={tab.path} header={tab.header}>
            {isMobile && tab.mobileSummary ? (
              <tab.mobileSummary
                specialtyPropertyInfo={safeSpecialtyPropertyInfo}
              />
            ) : tab.component.type === "safe" ? (
              <tab.component.component
                specialtyPropertyInfo={safeSpecialtyPropertyInfo}
                forPrint={false}
                setMapVisible={setMapVisible}
              />
            ) : (
              <>
                {tab.component.type === "safe_and_raw" ? (
                  <tab.component.component
                    rawSpecialtyPropertyInfo={rawSpecialtyPropertyInfo}
                    specialtyPropertyInfo={safeSpecialtyPropertyInfo}
                    forPrint={false}
                    setMapVisible={setMapVisible}
                  />
                ) : (
                  <tab.component.component
                    specialtyPropertyInfo={rawSpecialtyPropertyInfo}
                    forPrint={false}
                    setMapVisible={setMapVisible}
                  />
                )}
              </>
            )}
          </TabPanel>
        );
      })}
    </TabView>
  );
};

const LoadedReport: React.FC<{
  specialtyPropertyInfo: client.ReportResponse;
  safeResponse: SafeReportResponse;
}> = ({ specialtyPropertyInfo, safeResponse }) => {
  useLogUIEventOnMount("asgard_show_report");

  const clientId = useClientId();
  const me = useLocalUser();
  const [isQuoteIndicationModalVisible, setIsQuoteIndicationModalVisible] =
    useState(false);
  const [isPolicyFlyModalVisible, setIsPolicyFlyModalVisible] = useState(false);
  const [isMapModalVisible, setIsMapModalVisible] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const assignee = useUserById(specialtyPropertyInfo?.assignee);
  const handler = useUserById(
    specialtyPropertyInfo.additional_assignees?.assignees["handler"]
  );
  const activeBor = useBorForReportBadge(specialtyPropertyInfo.report_id);
  const allBors = useBorLettersByReport(specialtyPropertyInfo.report_id);
  const { showBorModal } = useBorModal();

  useEffect(() => {
    (async () => {
      if (isPrinting) {
        await sleepMs(0);
        window.print();
        await sleepMs(0);
        setIsPrinting(false);
      }
    })();
  }, [isPrinting]);

  const toast = useToast();
  const clearToastOnUnmount = useClearToastsOnUnmount();

  useEffect(() => {
    if (specialtyPropertyInfo.report_json.sovs.length === 0) {
      toast.show(
        clearToastOnUnmount({
          severity: "warn",
          summary: "No SOV Found",
          detail:
            'No SOV files were found when processing this submission. If you think an SOV was included, please classify it on the "Attachments" tab.',
          sticky: true,
        })
      );
    }
  }, [specialtyPropertyInfo.report_json.sovs.length]);

  const isRerunning = useThisReportIsUpdating(
    specialtyPropertyInfo.report_id,
    "RERUN_REPORT"
  );
  const rerunningUser = useUserById(isRerunning?.updating?.running_user_id);
  const rerunningUserName = displayNameForUser(rerunningUser) ?? "Someone";
  const cancelJobs = useCancelJobs();
  const rerunToast = useRef<ToastMessage | null>();
  useEffect(() => {
    if (isRerunning) {
      if (rerunToast.current) {
        toast.remove(rerunToast.current);
      }
      const handleCancel = () => {
        cancelJobs.mutate([isRerunning.updating.update_id], {
          onError: () => {
            toast.show({
              severity: "error",
              summary: "Cancel Failed",
              detail: "Failed to cancel re-processing.",
              life: 5000,
            });
          },
        });
      };
      const showCancelRerun = featureEnabled(clientId, "rerun-cancellation", {
        user: me,
      });
      rerunToast.current = {
        severity: "warn",
        sticky: true,
        content: (
          <div className={styles.rerunToastContent}>
            <i className={`pi pi-spin pi-spinner ${styles.rerunToastIcon}`} />
            <div className={styles.rerunToastBody}>
              <div className={styles.rerunToastSummary}>Re-processing</div>
              <div className={styles.rerunToastDetail}>
                {rerunningUserName} is re-processing this submission — this may
                take a few minutes. The data here may change when re-processing
                is finished.
              </div>
              {showCancelRerun ? (
                <Button
                  label="Cancel"
                  severity="warning"
                  size="small"
                  onClick={handleCancel}
                  loading={cancelJobs.isPending}
                  className={styles.rerunToastCancelBtn}
                />
              ) : null}
            </div>
          </div>
        ),
      };
      toast.show(clearToastOnUnmount(rerunToast.current));
    } else if (!isRerunning && rerunToast.current) {
      toast.remove(rerunToast.current);
      rerunToast.current = null;
    }
  }, [isRerunning, rerunningUserName, me]);

  const isPolicyFlyRunning = useThisReportIsUpdating(
    specialtyPropertyInfo.report_id,
    "POLICY_FLY"
  );
  const allPolicyFlyUpdates = useAllUpdatesForReport(
    specialtyPropertyInfo.report_id,
    "POLICY_FLY"
  );
  const policyFlyToast = useRef<ToastMessage | null>();
  useEffect(() => {
    if (isPolicyFlyRunning) {
      if (policyFlyToast.current) {
        toast.remove(policyFlyToast.current);
      }
      const handleCancelPolicyFly = () => {
        const updateIds = allPolicyFlyUpdates.map((u) => u.update_id);
        cancelJobs.mutate(updateIds, {
          onError: () => {
            toast.show({
              severity: "error",
              summary: "Cancel Failed",
              detail: "Failed to cancel Policy Fly generation.",
              life: 5000,
            });
          },
        });
      };
      policyFlyToast.current = {
        severity: "warn",
        sticky: true,
        content: (
          <div className={styles.rerunToastContent}>
            <i className={`pi pi-spin pi-spinner ${styles.rerunToastIcon}`} />
            <div className={styles.rerunToastBody}>
              <div className={styles.rerunToastSummary}>
                Generating Policy Fly Applications
              </div>
              <div className={styles.rerunToastDetail}>
                The Policy Fly applications are being generated — this may take
                a few minutes.
              </div>
              <Button
                label="Cancel"
                severity="warning"
                size="small"
                onClick={handleCancelPolicyFly}
                loading={cancelJobs.isPending}
                className={styles.rerunToastCancelBtn}
              />
            </div>
          </div>
        ),
      };
      toast.show(clearToastOnUnmount(policyFlyToast.current));
    } else if (!isPolicyFlyRunning && policyFlyToast.current) {
      toast.remove(policyFlyToast.current);
      policyFlyToast.current = null;
    }
  }, [isPolicyFlyRunning, allPolicyFlyUpdates]);

  const { privacyModeEnabled } = usePrivacyMode();
  const isMobile = useIsMobile();

  const reportName = privacyModeEnabled
    ? fakeCompanyNameFromCompanyName(
        safeResponse.report_json.company_info.company_name
      )
    : safeResponse.report_json.company_info.company_name;

  const supportsClusterAnalysis = useMemo(() => {
    return addressListSizeSupportsClusterAnalysis(
      safeResponse.report_json.sovs
        .filter((sov) => sov.is_enabled)
        .flatMap((sov) => sov.addresses).length
    );
  }, [safeResponse]);

  const quoteButton = (
    <Button
      severity="secondary"
      icon="pi pi-envelope"
      raised
      text
      tooltip="Generate quote indication"
      tooltipOptions={{ position: "top" }}
      onClick={() => setIsQuoteIndicationModalVisible(true)}
    />
  );

  const reportActionMenu = (
    <ReportActionMenu
      report={{
        report_metadata: {
          ...safeResponse.report_json,
          // Unused here, so we can just mock it
          precomputed_metadata: { num_locations: 0, total_tiv: 0 },
          lob: safeResponse.lob,
        },
        ...specialtyPropertyInfo,
      }}
      isUpcomingRenewal={TEMP_PIPELINE_EXAMPLES.has(
        safeResponse.report_json.company_info.company_name
      )}
      raised={true}
      navigateAfterDelete={true}
      onManageBor={
        allBors.data?.length
          ? () => showBorModal(specialtyPropertyInfo.report_id)
          : undefined
      }
    />
  );

  const actionButtons = (
    <>
      <IfFeatureEnabled feature="policy-fly-integration">
        <Button
          severity="secondary"
          icon={
            <img
              src="https://www.google.com/s2/favicons?domain=policyfly.com&sz=64"
              style={{ width: "20px", height: "20px" }}
            />
          }
          raised
          text
          tooltip="Policy Fly"
          tooltipOptions={{ position: "top" }}
          onClick={() => setIsPolicyFlyModalVisible(true)}
        />
      </IfFeatureEnabled>
      {quoteButton}
      {reportActionMenu}
    </>
  );

  // Mobile report tabs are view-only; the header keeps only the view-friendly
  // write actions — quote indication and the kebab menu (no Policy Fly).
  const mobileActionButtons = (
    <>
      {quoteButton}
      {reportActionMenu}
    </>
  );

  return (
    <>
      {isPrinting ? (
        <PrintableReport
          specialtyPropertyInfo={safeResponse}
          rawSpecialtyPropertyInfo={specialtyPropertyInfo}
        />
      ) : null}
      <div className={styles.container}>
        <div className={styles.verticalContainer}>
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: "backOut" }}
          >
            {isMobile ? (
              <MobileReportHeader
                safeResponse={safeResponse}
                specialtyPropertyInfo={specialtyPropertyInfo}
                reportName={reportName}
                assignee={assignee}
                handler={handler}
                bor={activeBor.data}
                actionButtons={mobileActionButtons}
              />
            ) : (
              <DesktopReportHeader
                safeResponse={safeResponse}
                specialtyPropertyInfo={specialtyPropertyInfo}
                reportName={reportName}
                assignee={assignee}
                handler={handler}
                bor={activeBor.data}
                clientId={clientId}
                me={me}
                showBorModal={showBorModal}
                actionButtons={actionButtons}
              />
            )}
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35, ease: "backOut", delay: 0.07 }}
          >
            <RoutedTabView
              rawSpecialtyPropertyInfo={specialtyPropertyInfo}
              safeSpecialtyPropertyInfo={safeResponse}
              setMapModalVisible={
                supportsClusterAnalysis ? setIsMapModalVisible : undefined
              }
            />
          </motion.div>
          {isMapModalVisible && (
            <MapModalForSubmission
              visible={isMapModalVisible}
              onHide={() => setIsMapModalVisible(false)}
              specialtyPropertyInfo={safeResponse}
            />
          )}
          <QuoteIndicationModal
            specialtyPropertyInfo={safeResponse}
            visible={isQuoteIndicationModalVisible}
            setVisible={setIsQuoteIndicationModalVisible}
          />
          <PolicyFlyModal
            visible={isPolicyFlyModalVisible}
            setVisible={setIsPolicyFlyModalVisible}
            specialtyPropertyInfo={safeResponse}
          />
        </div>
      </div>
    </>
  );
};

export const SpecialtyReport: React.FC<{ clientId: ClientId }> = ({
  clientId,
}) => {
  const { reportId } = useParams<{ reportId: string }>();

  const reportResponse = useReport(reportId);
  const specialtyPropertyInfo = reportResponse.isSuccess
    ? reportResponse.data
    : undefined;

  const safeResponse = useMemo(
    () =>
      specialtyPropertyInfo
        ? applyOverridesToResponse(specialtyPropertyInfo, clientId)
        : undefined,
    [specialtyPropertyInfo]
  );

  if (reportId === undefined) {
    // This should never happen
    return <></>;
  }

  // TODO: return some sort of failure state if we couldn't find the report
  if (specialtyPropertyInfo === undefined || safeResponse === undefined) {
    return <></>;
  }

  return (
    <LoadedReport
      safeResponse={safeResponse}
      specialtyPropertyInfo={specialtyPropertyInfo}
      key={reportId}
    />
  );
};
